import { useCallback, useEffect, useState } from "react";
import {
  useLeadsContainerStore,
  useLeadsStore,
} from "../../../../../stores/LeadsStore";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  editStatus,
  getStatusCustom,
  getStatusSystem,
} from "../../../../../api/PRMAPI";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { Button, Group, LoadingOverlay, Modal, Stack } from "@mantine/core";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import StatusItem from "./StatusItem";
import { createPortal } from "react-dom";
import { IconCircleCheck } from "@tabler/icons-react";
import CreateStatus from "./CreateStatus";
import { notifications } from "@mantine/notifications";
import { useAccountsStore } from "../../../../../stores/ConfigStore";
import { setupFilters } from "../../utils";

function handleSystemStatuses(systemStatus) {
  const lang = useLanguage.getState().language;
  const emails = useAccountsStore.getState().email_accounts;

  let map = {
    opener: lang.components.status_selector.statuses.opener,
    clicker: lang.components.status_selector.statuses.clicker,
    answerer: lang.components.status_selector.statuses.answerer,
    to_call: lang.components.status_selector.statuses.to_call,
    out_of_office: lang.components.status_selector.statuses.out_of_office,
  };

  // if (emails?.find(x => x.))

  let result = systemStatus?.map((status) => ({
    ...status,
    name: map[status.status],
    id: status.status,
  }));

  return systemStatus ? result : [];
}

const StatusContent = ({ opened, handleClose }) => {
  const lang = useLanguage((s) => s.language);
  const containers = useLeadsContainerStore((s) => s.containers);
  const setContainers = useLeadsContainerStore((s) => s.setContainers);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([...containers]);
  const [activeDrag, setActiveDrag] = useState(null);

  function handleDragStart(event) {
    setActiveDrag(items.find((x) => x.id === event.active.id));
  }

  function handleDragEnd(event) {
    const { active, over } = event;

    if (over) {
      if (active.id !== over.id) {
        setItems((items) => {
          const oldIndex = items.findIndex((x) => x.id === active.id);
          const newIndex = items.findIndex((x) => x.id === over.id);

          editStatus({
            id: active.id,
            sorting: newIndex + (oldIndex < newIndex ? 1 : 0),
            isSystem: !parseInt(active?.id),
          }).then(() => {
            notifications.show({
              message: lang.prm.status.edited,
              color: "tertiary.8",
            });
          });

          return arrayMove(items, oldIndex, newIndex);
        });
      }
    }
    setActiveDrag(null);
  }

  const handleDone = useCallback(() => {
    let diff = [];
    items
      .filter((x) => x.visible)
      .forEach((item) => {
        if (
          containers.filter((x) => !x.visible).find((x) => x.id === item.id) || // If status is hidden before
          !containers.find((x) => x.id === item.id) // If status didn't exist before
        ) {
          diff.push(item);
        }
      });
    setContainers(
      items.map((x) => ({
        ...x,
        loading: !!diff.find((d) => d.id === x.id),
      }))
    );

    setupFilters({
      datafields: useLeadsStore.getState().fields,
      custom_columns: items.map((x) => ({
        label: x.name,
        value: x.id.toString(),
      })),
    });

    handleClose();
  }, [setContainers, items, handleClose, containers]);

  const handleNewStatus = (id, name) => {
    setLoading(true);
  };

  useEffect(() => {
    if (loading) {
      getStatusSystem().then((system_data) => {
        getStatusCustom()
          .then((response) => {
            let customStatuses = response.data.status;
            const newItems = [
              ...handleSystemStatuses(system_data.data.status),
              ...customStatuses,
            ].map((x) => {
              let sameContainer = containers.find((con) => con.id === x.id);
              return {
                ...(sameContainer || {}),
                ...x,
              };
            });
            setItems(newItems.sort((a, b) => a?.sorting - b?.sorting));
          })
          .finally(() => {
            setLoading(false);
          });
      });
    }
  }, [loading, containers]);

  return (
    <Modal
      opened={opened}
      onClose={handleDone}
      title={lang.prm.status.dialog_title}
      size={"40%"}
    >
      <Stack pos={"relative"} gap={"xs"}>
        <LoadingOverlay visible={loading} />
        <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((item, index) => {
              return (
                <StatusItem
                  key={item.id}
                  id={item.id}
                  visible={item?.visible}
                  name={item.name}
                  color={item.color}
                  deletable={!item.status}
                  isCustomDefault={item.type_default_status}
                  setItems={setItems}
                  statuses={items}
                  stat={item.total}
                  setLoading={setLoading}
                />
              );
            })}
          </SortableContext>
          {createPortal(
            <DragOverlay>
              {activeDrag ? (
                <StatusItem
                  name={activeDrag.name}
                  visible={activeDrag?.visible}
                  color={activeDrag.color}
                  stat={activeDrag?.total || 0}
                  placeholder={activeDrag.name}
                  isOverlay
                />
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      </Stack>

      <Group justify="end" mt={"md"}>
        <CreateStatus handleDone={handleNewStatus} />

        <Button
          onClick={handleDone}
          leftSection={<IconCircleCheck size={22} />}
          color="tertiary.8"
        >
          {lang.prm.status.apply_label}
        </Button>
      </Group>
    </Modal>
  );
};

export default StatusContent;
